/**
 * 모자·소품 오버레이의 **놓을 자리** — 단일 정의.
 *
 * ## 왜 필요했나
 * 오버레이(`hero.hair.*`·`hero.acc.*`)와 룩(`hero.look.*`)은 둘 다 512 정사각이라
 * "그대로 겹치면 맞는다"고 두었다. 크기는 맞지만 **그림 속 물건의 크기와 자리까지
 * 맞는 건 아니다.** 실측해 보니 가방(a1)은 몸 옆에 뜬 채 통짜 룩 아트의 가방보다
 * 두 배 컸고, 랜턴(a3)은 몸과 9% 밖에 안 겹쳐 공중에 떠 있었다.
 *
 * 그림을 다시 그리는 대신 **놓는 자리를 고친다** — 크기·위치는 숫자 세 개면 되고,
 * 두 렌더러(`HeroAvatar` SVG · `WorldScene` 캔버스)가 같은 표를 보면 어긋날 일이 없다.
 *
 * ## 좌표 약속
 * - `scale` — 아바타 사각형 대비 배율(1 = 프레임 가득).
 * - `dx`,`dy` — **프레임 한 변 대비 비율**로 중심에서 옮기는 양(오른쪽·아래가 +).
 * 즉 아바타를 어떤 크기로 그리든 같은 자리에 앉는다.
 *
 * ## 좌우 반전
 * 옆모습으로 왼쪽을 볼 때 룩이 통째로 뒤집힌다. 그때 `dx` 도 같이 뒤집어야
 * 오른쪽 허리의 가방이 왼쪽 허리로 따라간다 — `overlayOffsetX` 를 쓸 것.
 *
 * ## 여기 없는 키는 손대지 않는다
 * c1·c2·c3(탐험가 모자·비니·금관)은 지금 자리가 맞다. 키워 봤더니 프레임 위로
 * 잘리거나 눈을 덮었다 — **문제 없는 것을 건드리지 않는다.**
 */

// ⚠️ 확장자(.ts)를 붙인다 — `scripts/hero-overlay-check.mjs` 가 이 파일을
// `node --experimental-strip-types` 로 직접 읽는데, 그 모드는 확장자 없는 상대 경로를 못 푼다.
import { anchoredFit, grippedFit, anchorPartOf, anchorBodyOf, HELD_GRIP } from "./heroAnchor.ts";

export interface OverlayFit {
  scale: number;
  dx: number;
  dy: number;
  /** 기울임(도). 몸 기울기와 **별개**로 이 물건만 돌린다(가방 각도 등). */
  rot: number;
  /** 좌우 뒤집기. 몸의 반전과 **곱해진다** — 왼쪽을 보면 여기 true 여도 원래대로 돌아온다. */
  flipX: boolean;
}

const NEUTRAL: OverlayFit = { scale: 1, dx: 0, dy: 0, rot: 0, flipX: false };

/** 조절 범위 — 이 밖으로 나가면 그림이 프레임에서 잘리거나 몸을 덮는다. */
export const OVERLAY_FIT_LIMITS = {
  scaleMin: 0.3,
  scaleMax: 1.2,
  /** |dx|,|dy| 상한(프레임 대비 비율). */
  offset: 0.25,
  /** |rot| 상한(도). */
  rot: 30,
} as const;

/**
 * 값은 네 몸(b1~b4) × 오버레이 전 조합을 실제로 합성해 보고 정한 것이다.
 * 바꿀 땐 `node scripts/hero-overlay-check.mjs` 로 다시 재 볼 것.
 */
export const OVERLAY_FIT: Readonly<Record<string, Partial<OverlayFit>>> = {
  // 모자(c1~c4)는 **그림 자체를 머리 폭에 맞춰 다시 그렸다**(2026-07-28).
  //
  // 그림 위에 여백이 0.4% 뿐이라 조금만 위로 밀어도 정수리가 잘렸다. 그래서 2026-07-29 에
  // **그림 내용을 프레임 안에서 12% 아래로 밀어 위쪽에 여백을 만들었다.** 아래 `dy: -0.12` 는
  // 그 이동을 정확히 상쇄한다 — 화면에 보이는 자리는 그대로고, 위로 12%까지 올릴 여유만 생긴다.
  // ⚠️ 모자 그림을 새로 만들 때도 **위쪽 12% 를 비워 둘 것**(안 비우면 다시 잘린다).
  "hero.hair.c1": { dy: -0.12 },
  "hero.hair.c2": { dy: -0.12 },
  "hero.hair.c3": { dy: -0.12 },
  "hero.hair.c4": { dy: -0.12 },

  // 크로스백 — 통짜 룩(o2)의 가방보다 두 배 컸고 몸 옆에 떠 있었다.
  // 줄여서 허리에 붙이면 어깨끈 토막이 가방 자체의 잠금끈처럼 읽힌다.
  "hero.acc.a1": { scale: 0.6, dx: -0.03, dy: 0.09 },
  // 별 배지 — 어깨 밖으로 반쯤 나가 있었다. 가슴 위로.
  "hero.acc.a2": { scale: 0.78, dx: 0.035, dy: 0.055 },
  // 손등 랜턴 — 몸과 9% 밖에 안 겹쳐 옆에 둥둥 떠 있었다. 손 쪽으로 당긴다.
  "hero.acc.a3": { scale: 0.72, dx: -0.07, dy: 0.03 },

  // ── 방향별 자리 ──────────────────────────────────────
  // 그림은 정면 한 장을 그대로 쓰되(모자·가방은 옆·뒤에서도 형태가 크게 안 바뀐다)
  // **놓는 자리만** 방향에 맞춘다. 정면 값을 그대로 두면 옆을 보고 걷는데 가방이
  // 배 앞을 가로지르고, 랜턴이 가슴에 붙는다.
  "hero.acc.a1.side": { scale: 0.54, dx: -0.08, dy: 0.1 }, // 옆: 뒤쪽 엉덩이로 물러난다
  "hero.acc.a1.up": { scale: 0.56, dx: 0, dy: 0.09 }, // 뒤: 등 아래 한가운데
  "hero.acc.a3.side": { scale: 0.66, dx: -0.02, dy: 0.05 }, // 옆: 손 높이로 내린다
  // 뒤: 손 옆으로. 눈대중으로 0.09(바깥쪽)를 골랐다가 `hero:check` 에 걸렸다 —
  // 겹침 0%, 몸 밖 61px. 뒷모습 프레임은 몸이 더 좁아서 바깥으로 밀면 허공에 뜬다.
  "hero.acc.a3.up": { scale: 0.62, dx: -0.08, dy: 0.04 },
};

/** 이 오버레이를 놓을 자리(정면). 표에 없으면 손대지 않는다(프레임 그대로). */
export function overlayFit(key: string, doc?: OverlayFitDoc): OverlayFit {
  return { ...NEUTRAL, ...OVERLAY_FIT[key], ...doc?.[key] };
}

/** 좌우 반전을 반영한 가로 이동량 — 뒤집힌 몸에서는 가방도 반대쪽 허리로 가야 한다. */
export function overlayOffsetX(key: string, flipped: boolean, doc?: OverlayFitDoc): number {
  const dx = overlayFit(key, doc).dx;
  if (!flipped || dx === 0) return dx; // -0 을 만들지 않는다(비교가 어긋난다)
  return -dx;
}

// ── 방향별 오버레이 ────────────────────────────────────────
/**
 * 몸이 보는 쪽. 걷기 스트립 규약과 같다(`<키>.down/.side/.up`, 왼쪽 = side + 좌우반전).
 */
export type OverlayDir = "down" | "side" | "up";

/**
 * **그 방향에서는 아예 안 그리는** 오버레이.
 *
 * 정면 그림 한 장을 모든 방향에 붙이면, 뒤돌아선 아이의 등에 가슴 배지가 뜬다.
 * 가려질 물건은 그리지 않는 게 맞다 — 뒷모습용 그림을 따로 만들 이유도 없다.
 */
export const OVERLAY_HIDDEN: Readonly<Record<string, readonly OverlayDir[]>> = {
  "hero.acc.a2": ["up"], // 가슴에 단 별 배지 — 뒤에서는 안 보인다
  "hero.badge.medal": ["up"], // 완주 메달도 가슴에 건다
  // 완주 뱃지 소품. 커스터마이저에는 안 나오지만(REWARD_ONLY_ACC) **파츠 문자열로는 올 수 있다** —
  // 예전에 저장된 값이나 원격 유저의 파츠. 목에 거는 물건이라 뒤에서는 안 보여야 하는데,
  // 여기 없어서 뒷모습 등에 메달이 떠 있었다(네 아이 전부).
  "hero.acc.a4": ["up"],
};

/**
 * 이 방향에서 쓸 그림 키 **후보**(앞이 우선, 실제로 게시된 첫 번째를 쓴다).
 *
 * 방향 그림(`<키>.side`·`<키>.up`)이 없으면 **정면 그림으로 폴백**한다 —
 * 운영자가 새 모자를 정면 한 장만 올려도 오늘과 똑같이 동작하고,
 * 방향 그림을 나중에 올리면 코드 변경 없이 그때부터 쓰인다(콘텐츠 주도).
 *
 * 빈 배열이면 그 방향에서는 그리지 않는다(OVERLAY_HIDDEN).
 */
export function overlayDirKeys(key: string, dir: OverlayDir): string[] {
  if ((OVERLAY_HIDDEN[key] ?? []).includes(dir)) return [];
  return dir === "down" ? [key] : [`${key}.${dir}`, key];
}

/** 한 오버레이가 쓸 수 있는 모든 키(미리 받아 둘 목록). */
export function overlayAllKeys(key: string): string[] {
  return [key, `${key}.side`, `${key}.up`];
}

/**
 * 방향까지 반영한 놓을 자리.
 *
 * **방향 그림이 없어도 자리는 방향마다 다를 수 있다.** 옆을 보고 걸을 때 허리 가방은
 * 몸 뒤쪽으로 물러나야 하고, 뒤돌아서면 등 한가운데로 와야 한다. 이건 그림을 새로
 * 그리지 않아도 고칠 수 있는 부분이라 표에서 먼저 잡는다(`<키>.side`·`<키>.up`).
 * 방향 항목이 없으면 정면 값을 그대로 쓴다.
 */
export function overlayFitFor(key: string, dir: OverlayDir, doc?: OverlayFitDoc): OverlayFit {
  if (dir !== "down") {
    const dirKey = `${key}.${dir}`;
    // 방향 항목은 **정면 값을 대체**한다(합치지 않는다) — 반쯤 섞이면 어디서 온 값인지 알 수 없다.
    if (OVERLAY_FIT[dirKey] || doc?.[dirKey]) {
      return { ...NEUTRAL, ...OVERLAY_FIT[dirKey], ...doc?.[dirKey] };
    }
  }
  return overlayFit(key, doc);
}

/** 방향 + 좌우반전을 반영한 가로 이동량. */
export function overlayOffsetXFor(
  key: string,
  dir: OverlayDir,
  flipped: boolean,
  doc?: OverlayFitDoc,
): number {
  const dx = overlayFitFor(key, dir, doc).dx;
  if (!flipped || dx === 0) return dx;
  return -dx;
}

// ── 운영자 조절값(관리자에서 저장) ──────────────────────────
/**
 * 운영자가 관리자에서 맞춘 값. **코드 기본값 위에 덮어쓴다** —
 * 게시 전이면 지금까지의 동작 그대로다(불변식 3: 서버 없어도 돌아간다).
 *
 * 키는 정면(`hero.hair.c1`)이거나 방향별(`hero.acc.a1.side`)이다.
 */
export type OverlayFitDoc = Record<string, Partial<OverlayFit>>;

/**
 * 조절 가능한 오버레이 키인가 — 모르는 키는 버린다(주입·오타 방지).
 *
 * 세 가지 모양을 받는다:
 *   `hero.hair.c1`            모든 아이에게 적용
 *   `hero.hair.c1.side`       그 방향만
 *   `b3|hero.hair.c1.side`    **그 아이만** — 자동 보정으로 안 잡히는 것(망토 위 가방 등)
 */
/**
 * 운영자가 **직접 만든** 모자·소품의 키인가 — `hero.hair.c9`·`hero.acc.a12`.
 *
 * 🔴 이게 없어서 **맞춰 둔 자리가 저장될 때마다 사라졌다**(2026-08-23 신고).
 *
 * 모자·소품을 몇 개든 만들 수 있게 바꾸면서(매니페스트가 목록이다) `HERO_CATALOG` 는
 * 기본 제공분만 남겼는데, **저장값이 살아남는 화이트리스트는 그대로 카탈로그를 봤다.**
 * 그래서 운영자가 새 모자의 자리를 정성껏 맞추고 저장하면 정화 단계에서 조용히 버려졌다 —
 * 저장은 성공한 것처럼 보이고, 다시 열면 처음 값이다. 어디가 틀렸는지 알 길이 없다.
 *
 * 목록 대신 **모양**으로 본다(오늘 `isBase`·파츠 토큰을 고친 것과 같은 이유).
 * 키는 매니페스트 조회에만 쓰이므로, 모양만 맞으면 없는 그림은 그냥 안 그려진다.
 */
const OPEN_PART_KEY_RE = /^hero\.(hair|acc)\.[a-z][0-9]{1,3}$/;

export function isOverlayFitKey(key: string, valid: readonly string[]): boolean {
  const base = overlayFitItemKey(key).replace(/\.(side|up)$/, "");
  const known = valid.includes(base) || OPEN_PART_KEY_RE.test(base);
  return known && /^(b\d+)?$/.test(overlayFitBodyKey(key));
}

/** `b3|hero.hair.c1.side` 형태에서 아이만. 아이 지정이 없으면 빈 문자열. */
export function overlayFitBodyKey(key: string): string {
  const i = key.indexOf(OVERLAY_FIT_SEP);
  return i < 0 ? "" : key.slice(0, i);
}

/** `b3|hero.hair.c1.side` 형태에서 아이템(방향 포함)만. */
export function overlayFitItemKey(key: string): string {
  const i = key.indexOf(OVERLAY_FIT_SEP);
  return i < 0 ? key : key.slice(i + 1);
}

/**
 * 아이 지정 구분자. 키에 이미 `.` 가 쓰이고 있어서(`hero.hair.c1.side`) 다른 글자를 쓴다 —
 * `.` 로 이으면 `hero.hair.c1.side` 인지 `b3` 인지 파싱이 갈린다.
 */
export const OVERLAY_FIT_SEP = "|";

/** 이 아이·이 아이템의 저장 키. 관리자와 렌더러가 **같은 함수**로 만든다(오타 방지). */
export function overlayFitKeyFor(item: string, dir: OverlayDir, body?: string): string {
  const withDir = dir === "down" ? item : `${item}.${dir}`;
  return body ? `${body}${OVERLAY_FIT_SEP}${withDir}` : withDir;
}

const clampNum = (v: unknown, lo: number, hi: number, fallback: number): number => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
};

/**
 * 외부 입력(D1 문서·관리자 폼) → 안전한 조절값. **절대 throw 하지 않는다**(불변식 7).
 *
 * @param valid 허용할 오버레이 키 목록(카탈로그에서 만든 것). 없으면 키 검사를 건너뛴다.
 */
export function sanitizeOverlayFitDoc(input: unknown, valid?: readonly string[]): OverlayFitDoc {
  if (!input || typeof input !== "object") return {};
  const L = OVERLAY_FIT_LIMITS;
  const out: OverlayFitDoc = {};
  let n = 0;
  for (const [key, raw] of Object.entries(input as Record<string, unknown>)) {
    // 손상 입력이 표를 무한정 늘리지 못하게. 모자·소품은 이제 **몇 개든 만들 수 있으므로**
    // 조합 수가 고정이 아니다(아이템 × 방향 3 × (전체 + 아이 4)). 넉넉히 잡되 상한은 둔다 —
    // 운영자가 20개를 만들어도 20×3×5=300 이다.
    if (n >= 2000) break;
    if (valid && !isOverlayFitKey(key, valid)) continue;
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const fit: Partial<OverlayFit> = {};
    if (o.scale !== undefined) fit.scale = clampNum(o.scale, L.scaleMin, L.scaleMax, 1);
    if (o.dx !== undefined) fit.dx = clampNum(o.dx, -L.offset, L.offset, 0);
    if (o.dy !== undefined) fit.dy = clampNum(o.dy, -L.offset, L.offset, 0);
    if (o.rot !== undefined) fit.rot = clampNum(o.rot, -L.rot, L.rot, 0);
    if (o.flipX !== undefined) fit.flipX = o.flipX === true;
    if (Object.keys(fit).length === 0) continue;
    out[key] = fit;
    n++;
  }
  return out;
}

// ── 아이별 보정까지 적용한 최종 배치 ────────────────────────
/**
 * **렌더러가 실제로 써야 하는 함수.** 아이템 표 → 이 아이의 머리·가슴 자리에 맞춘 배치.
 *
 * `overlayFitFor` 는 "평균적인 아이" 기준 값이다. 아이마다 머리 크기와 턱 높이가 달라서
 * (턱 높이 ±11.7%) 그 값을 그대로 쓰면 가슴 배지가 어떤 아이는 목에, 어떤 아이는 배에 붙는다.
 * 보정 규칙은 `heroAnchor` 에 있고 값은 아트에서 잰다(`pnpm hero:anchors`).
 *
 * @param look 통짜 룩 키나 몸 id(`hero.look.b3_o1` · `b3`). 모르는 값이면 보정 없음.
 */
export function overlayFitOn(
  key: string,
  dir: OverlayDir,
  look: string,
  doc?: OverlayFitDoc,
): OverlayFit {
  const body = anchorBodyOf(look);
  // 「이 아이만」 값이 있으면 **자동 보정 대신** 그 값을 쓴다(합치지 않는다).
  // 운영자가 눈으로 맞춘 값 위에 자동 보정을 또 얹으면 화면과 관리자 미리보기가 어긋난다 —
  // 운영자는 이미 그 아이를 보면서 맞췄으니 보정은 그 안에 들어 있다.
  if (body) {
    // 그 방향 값 → 없으면 그 아이의 정면 값(아이템 표의 방향 폴백과 같은 규칙)
    const own =
      doc?.[overlayFitKeyFor(key, dir, body)] ??
      (dir === "down" ? undefined : doc?.[overlayFitKeyFor(key, "down", body)]);
    if (own) return { ...NEUTRAL, ...OVERLAY_FIT[key], ...own };
  }
  const base = overlayFitFor(key, dir, doc);
  // 손에 쥐는 물건(랜턴)은 손잡이를 손에 갖다 놓는다 — 몸에 붙이는 것과 규칙이 다르다
  const grip = HELD_GRIP[key];
  // ⚠️ 여기서 프레임 안으로 **밀어 넣지 않는다**.
  // 한때 넘치면 되밀었는데, 그러면 운영자가 관리자에서 모자를 위로 올려도 **되돌아가서
  // 슬라이더가 통째로 안 먹었다**. 막아 주는 이득은 기본 상태에서 2% 남짓인데 대가가 너무 크다.
  // 대신 아바타 SVG 가 프레임 밖을 안 자르게 했다(`overflow: visible`) — 넘쳐도 보인다.
  return grip
    ? grippedFit(base, look, dir, grip)
    : anchoredFit(base, look, dir, anchorPartOf(key));
}

/** 아이별 보정 + 방향 + 좌우반전을 반영한 가로 이동량. */
export function overlayOffsetXOn(
  key: string,
  dir: OverlayDir,
  look: string,
  flipped: boolean,
  doc?: OverlayFitDoc,
): number {
  const dx = overlayFitOn(key, dir, look, doc).dx;
  if (!flipped || dx === 0) return dx; // -0 을 만들지 않는다(비교가 어긋난다)
  return -dx;
}

/**
 * 이 방향에서 **무엇이 그려지는가**.
 *
 * - `own` — 그 방향 전용 그림이 있다
 * - `fallback` — 없어서 **앞 그림을 그대로 쓴다**(옆·뒤에서 어색할 수 있다)
 * - `hidden` — 그 방향에선 아예 안 그린다(가슴 배지 등, 없는 게 맞다)
 */
export type OverlayDirState = "own" | "fallback" | "hidden";

/**
 * 관리자에게 **사실만** 알려주기 위한 판정. "이건 옆 그림이 꼭 필요하다" 같은 취향은 코드가 정하지
 * 않는다 — 비니처럼 돌려도 같은 모양인 모자는 앞 그림 폴백이 정답이고, 무엇이 정답인지는
 * 그림을 보는 사람이 안다. 여기선 "지금 무엇이 쓰이는지"만 말한다.
 *
 * `hasArt(key)` 는 그 키의 그림이 실제로 있는지(게시된 매니페스트 기준).
 */
export function overlayDirState(
  key: string,
  dir: OverlayDir,
  hasArt: (artKey: string) => boolean,
): OverlayDirState {
  const cands = overlayDirKeys(key, dir);
  if (cands.length === 0) return "hidden";
  return hasArt(cands[0]!) ? "own" : "fallback";
}

/** 세 방향을 한 번에 — 관리자 목록에 그대로 뿌리기 좋게. */
export function overlayDirReport(
  key: string,
  hasArt: (artKey: string) => boolean,
): { dir: OverlayDir; state: OverlayDirState }[] {
  return (["down", "side", "up"] as const).map((dir) => ({
    dir,
    state: overlayDirState(key, dir, hasArt),
  }));
}
